import { FlowNode } from "./types";
import { JourneyFlowEdge, JourneyFlowNode, JourneyFlowResult } from "./journey-api";
import { BRANCHING_TYPES, findActionDef } from "./catalog";

// Converts a real GET /v1/bot/journeys/{id} response (flat nodes/edges) into
// this builder's own recursive chain model, so picking a ready-made journey
// in the "Create New Bot Journey" modal renders its exact prebuilt canvas
// (e.g. loan_abandonment_recovery's 24h/48h/24h reminder cadence ending in a
// Create CRM Task hand-off) instead of the same blank default chain every
// fresh journey used to get. Trigger resolution is untouched — it stays on
// the existing findDefaultTrigger/JOURNEY_CATEGORIES path in builder-page.tsx
// — this only rebuilds the chain that follows it.

const TERMINAL_TYPES = new Set(["end_workflow", "end"]);

// Backend wait unit ("hour"/"minute"/"day") -> this builder's own vocabulary
// ("hrs"/"min"/"days" — see catalog.ts's wait default + config-panel.tsx's
// unit <select> options).
const WAIT_UNIT_MAP: Record<string, string> = {
  hour: "hrs",
  hours: "hrs",
  hr: "hrs",
  hrs: "hrs",
  minute: "min",
  minutes: "min",
  min: "min",
  mins: "min",
  day: "days",
  days: "days",
};

const DEFAULT_CLASSIFY_INSTRUCTION =
  "Classify the supplied balance into exactly one of the configured tiers. Use the account currency and balance timestamp. Do not infer customer wealth, affordability or product suitability from the balance alone. If the balance is missing, stale or the currency is unsupported, return unknown.";
// churn_risk_escalation's RM step (queue "rm_retention") — the design's
// follow-up note for whoever picks the account up.
const RETENTION_TASK_MESSAGE =
  "Churn risk follow-up\nCustomer: {{customer.name}}\nCustomer type: {{customer.type}}\nRisk: {{churn_score}}\nLTV: {{ltv}}\n\nPlease reach out to understand the customer\u2019s concerns and discuss a suitable retention offer.";
// churn_risk_escalation's postback values, named as the design's pills.
const POSTBACK_LABELS: Record<string, string> = { self_serve: "Others", high_value: "Premium" };
const DEFAULT_DECIDE_INSTRUCTION = "Choose whether the customer should receive a savings offer, a fixed-deposit offer, or no offer.";

function mapNodeConfig(type: string, config: Record<string, any>): Record<string, any> {
  switch (type) {
    case "wait":
    case "wait_until_event":
      return {
        value: config.amount ?? config.value ?? 1,
        unit: WAIT_UNIT_MAP[String(config.unit || "").toLowerCase()] || config.unit || "hrs",
        thenBehavior: "continue",
      };
    case "condition":
      // Real shape: {attribute, operator, true_value, false_value} — mapped
      // onto this builder's "boolean" condition mode (one shared CHECK-If
      // attribute, one operator+value per branch), which already matches the
      // reference design's CHECK If / TRUE / FALSE panel (config-panel.tsx).
      return {
        mode: "boolean",
        booleanCheck: config.attribute || "",
        booleanTrueOperator: config.operator || "is_equal",
        booleanTrueValue: config.true_value ?? "",
        booleanFalseOperator: config.operator || "is_equal",
        booleanFalseValue: config.false_value ?? "",
      };
    // Real shape: {basis: "lifetime_value"} — its `options` list isn't config
    // at all on the real backend (see buildNode's own ai_decide handling,
    // which derives options from this node's outgoing postback edges instead).
    case "create_crm_task":
      return /retention/i.test(config.queue || "") && !config.message ? { ...config, message: RETENTION_TASK_MESSAGE, delivery: "immediately" } : { ...config };
    case "ai_decide":
      if (config.basis === "lifetime_value" && !config.instruction) {
        return { decideAttribute: config.basis, instruction: "Choose the appropriate churn-risk intervention based on customer value." };
      }
      return Object.keys(config).length === 0
        ? { decideAttribute: "Offer type", instruction: DEFAULT_DECIDE_INSTRUCTION, options: [] }
        : { decideAttribute: config.basis || "", ...(config.instruction ? { instruction: config.instruction } : {}) };
    // dormancy_reactivation ships ai_classify/ai_decide with `config: {}` —
    // seeded with the design's instructions so the cards open ready to run.
    case "ai_classify":
      return Object.keys(config).length === 0
        ? { classifyAttribute: "", instruction: DEFAULT_CLASSIFY_INSTRUCTION, tiers: [] }
        : { ...config, classifyAttribute: config.classifyAttribute ?? config.input ?? "" };
    // "branch" (parallel fan-out, e.g. {mode: "parallel", timeout_ms}) needs
    // no mapping — config-panel.tsx's Branch Type radios read `mode` as-is.
    default:
      return { ...config };
  }
}

const humanize = (s: string) => s.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function buildNode(id: string, nodesById: Map<string, JourneyFlowNode>, edgesBySource: Map<string, JourneyFlowEdge[]>, visited: Map<string, number>): FlowNode | null {
  const raw = nodesById.get(id);
  if (!raw || TERMINAL_TYPES.has(raw.type)) return null;

  // A real node can be reached from more than one parent when two branches
  // reconverge further down (confirmed live: salary_day_loan's AI Moderate
  // sends "approved template" vs. "AI-personalised" down two different
  // paths that both lead back to the same Wait 48hr -> Engaged? -> Write
  // outcome back tail). This builder's chain is a strict one-parent-per-node
  // tree, so a second encounter of the same backend node gets its own
  // suffixed id rather than reusing the first — same type/config/label, but
  // kept distinguishable so editing one copy on canvas can't silently affect
  // (or get overwritten by) the other, identically-id'd instance sitting in
  // a different branch.
  const seen = visited.get(id) || 0;
  visited.set(id, seen + 1);
  const nodeId = seen === 0 ? raw.id : `${raw.id}__dup${seen}`;

  const def = findActionDef(raw.type);
  const node: FlowNode = {
    id: nodeId,
    type: raw.type,
    category: def?.category ?? "data",
    // The canvas card header should read as the generic action name ("AI
    // Decide", "AI Classify", "Fetch Profile" — same as a freshly-dragged
    // node from the Add panel gets via makeFlowNode), not this journey's own
    // per-step narrative label (e.g. churn_risk_escalation's "Self-serve vs
    // RM outreach") — that journey-specific wording belongs in the
    // card's content row instead (see flow-canvas.tsx's per-type summaries),
    // not the title.
    label: def?.nodeLabel || raw.label || raw.type,
    config: mapNodeConfig(raw.type, raw.config || {}),
    next: null,
  };

  const outgoing = edgesBySource.get(id) || [];

  if (BRANCHING_TYPES.has(raw.type)) {
    // TODO: every real backend condition node confirmed live so far only
    // ever returns "true"/"false" edges, so this stays correct as-is for now.
    // If the backend ever returns `postback`-style edges off a real
    // `condition` node (the new named-group shape — see
    // isGroupedCondition/migrateConditionConfig, flow-model.ts), this needs
    // the same treatment `ai_decide` already gets just below: synthesize
    // `config.conditionGroups` from the edges' own `value`s and populate
    // `optionBranches` instead of `.branches`.
    //
    // A "branch" node's children are "parallel"-condition edges (both fire
    // together, confirmed against fraud_anomaly_alert's Send SMS + Send
    // Push) rather than "true"/"false" — mapped positionally onto this
    // builder's existing {true,false} storage (flow-canvas.tsx renders them
    // without TRUE/FALSE pills for this node type). Only the first two
    // parallel edges are kept; this model has no slot for a third.
    const parallelEdges = outgoing.filter((e) => e.condition === "parallel");
    const trueEdge = parallelEdges[0] || outgoing.find((e) => e.condition === "true");
    const falseEdge = parallelEdges[1] || outgoing.find((e) => e.condition === "false");
    // A few real condition nodes (salary_day_loan's "Churn band"/"Credit band
    // sets the rate") carry both a true AND a false edge that target the
    // exact same next step — they classify something used elsewhere in
    // config but don't actually fork the canvas. Building two separate
    // branches for a convergent pair would reconstruct everything downstream
    // twice (this tree model can't give one node two parents without the
    // id-suffixing above), so it collapses to one shared `.next` instead.
    // A branching type wired with one plain "always" edge and no true/false
    // pair (dormancy_reactivation's Compliance check -> Send) just continues
    // — building empty true/false branches from it dropped the Send.
    const alwaysEdge = !trueEdge && !falseEdge ? outgoing.find((e) => e.condition === "always") : undefined;
    // An AI Moderate is a gate, though: its continuation is the TRUE side
    // (passed) and a refusal ends the run. As a plain `.next` it went out as
    // a convergent true+false pair, which the compiler hoists past the
    // condition — the Send ran whether moderation passed or not (backend,
    // journey 346, 28 Oct).
    if (alwaysEdge && raw.type === "ai_moderate") {
      const targetRaw = nodesById.get(alwaysEdge.target);
      const next = targetRaw && !TERMINAL_TYPES.has(targetRaw.type) ? buildNode(alwaysEdge.target, nodesById, edgesBySource, visited) : null;
      node.branches = { true: { behavior: next ? "continue" : "end", next }, false: { behavior: "end", next: null } };
      node.config.trueBehavior = node.branches.true.behavior;
      node.config.falseBehavior = "end";
    } else if (alwaysEdge) {
      const targetRaw = nodesById.get(alwaysEdge.target);
      node.next = targetRaw && !TERMINAL_TYPES.has(targetRaw.type) ? buildNode(alwaysEdge.target, nodesById, edgesBySource, visited) : null;
    } else if (trueEdge && falseEdge && trueEdge.target === falseEdge.target) {
      node.next = buildNode(trueEdge.target, nodesById, edgesBySource, visited);
    } else {
      const buildBranch = (edge?: JourneyFlowEdge): { behavior: "end" | "continue"; next: FlowNode | null } => {
        if (!edge) return { behavior: "continue", next: null };
        const targetRaw = nodesById.get(edge.target);
        if (!targetRaw || TERMINAL_TYPES.has(targetRaw.type)) return { behavior: "end", next: null };
        return { behavior: "continue", next: buildNode(edge.target, nodesById, edgesBySource, visited) };
      };
      node.branches = { true: buildBranch(trueEdge), false: buildBranch(falseEdge) };
      // Mirror each branch's end/continue choice into the fields the boolean
      // condition panel reads/writes (see config-panel.tsx + flow-model.ts's
      // setBranchBehavior, which keeps these two in sync from then on).
      node.config.trueBehavior = node.branches.true.behavior;
      node.config.falseBehavior = node.branches.false.behavior;
    }
  } else if (raw.type === "ai_decide" && outgoing.some((e) => e.condition === "postback" && e.value)) {
    // A linear AI Decide (dormancy_reactivation: dec -> gen "always", the
    // decision only feeds AI Generate) skips this and continues as a plain
    // step below — mapping it here left zero branches and cut off every
    // step after it.
    //
    // Real shape confirmed against GET /v1/bot/journeys/churn_risk_escalation:
    // each option is a `condition: "postback"` edge carrying which option
    // (`value`, e.g. "self_serve") — there's no `options` list in config at
    // all server-side, so it's synthesized here from the edges themselves,
    // labeled from whatever the target step is actually called (more
    // descriptive than humanizing the raw postback value, which is only used
    // as a fallback when a path has no target yet).
    const postbackEdges = outgoing.filter((e) => e.condition === "postback" && e.value);
    node.config.options = postbackEdges.map((e) => ({
      id: e.value,
      label: POSTBACK_LABELS[e.value!] || nodesById.get(e.target)?.label || humanize(e.value!),
      description: "",
    }));
    node.optionBranches = postbackEdges.map((e) => {
      const targetRaw = nodesById.get(e.target);
      if (!targetRaw || TERMINAL_TYPES.has(targetRaw.type)) return { optionId: e.value!, behavior: "end" as const, next: null };
      return { optionId: e.value!, behavior: "continue" as const, next: buildNode(e.target, nodesById, edgesBySource, visited) };
    });
  } else {
    // A Send node's own "fallback" edge (pending backend deploy, per direct
    // ask) — the "always" edge is the delivered continuation, "fallback" is
    // a separate path taken when delivery itself fails (e.g. salary_day_
    // loan's "Preferred channel?" email/SMS split). Not a BRANCHING_TYPES
    // node type, so this is detected structurally here rather than by type —
    // same reasoning as holdout/ai_moderate's own divergent edges earlier:
    // without this, the fallback subtree would be silently dropped the exact
    // same way theirs was. Reused via `.branches` (true = always/delivered,
    // false = fallback) so flow-canvas.tsx's generic `node.branches` renderer
    // picks it up with no further changes; its own DELIVERED/FALLBACK pill
    // labels live there, keyed off node.type being a Send type.
    const alwaysEdge = outgoing.find((e) => e.condition === "always");
    const fallbackEdge = outgoing.find((e) => e.condition === "fallback");
    if (alwaysEdge && fallbackEdge && alwaysEdge.target !== fallbackEdge.target) {
      const buildBranch = (edge?: JourneyFlowEdge): { behavior: "end" | "continue"; next: FlowNode | null } => {
        if (!edge) return { behavior: "continue", next: null };
        const targetRaw = nodesById.get(edge.target);
        if (!targetRaw || TERMINAL_TYPES.has(targetRaw.type)) return { behavior: "end", next: null };
        return { behavior: "continue", next: buildNode(edge.target, nodesById, edgesBySource, visited) };
      };
      node.branches = { true: buildBranch(alwaysEdge), false: buildBranch(fallbackEdge) };
      node.config.trueBehavior = node.branches.true.behavior;
      node.config.falseBehavior = node.branches.false.behavior;
    } else {
      const nextEdge = alwaysEdge || outgoing[0];
      node.next = nextEdge ? buildNode(nextEdge.target, nodesById, edgesBySource, visited) : null;
    }
  }

  return node;
}

export function chainFromJourneyFlow(journey: JourneyFlowResult): FlowNode | null {
  const nodesById = new Map(journey.flow.nodes.map((n) => [n.id, n]));
  const edgesBySource = new Map<string, JourneyFlowEdge[]>();
  journey.flow.edges.forEach((e) => {
    const list = edgesBySource.get(e.source) || [];
    list.push(e);
    edgesBySource.set(e.source, list);
  });

  const triggerNode = journey.flow.nodes.find((n) => n.type === "trigger");
  if (!triggerNode) return null;
  const afterTrigger = (edgesBySource.get(triggerNode.id) || [])[0];
  if (!afterTrigger) return null;

  return buildNode(afterTrigger.target, nodesById, edgesBySource, new Map<string, number>());
}

// Reopening an already-created bot from "All Bots" for further editing.
// GET /journeys/{id}'s `definition` is a generic, backend-untyped object
// (confirmed live — no fixed schema), so its actual shape depends on how it
// was saved:
//  - This builder's own handlePublish now also writes a `_canvas` key onto
//    it holding the exact {startNodeId, nodes, edges} graph the canvas last
//    serialized — if present, that's a byte-perfect reconstruction, same
//    path as a prebuilt template.
//  - A bot saved before that (or via the older create-builder) only has the
//    flat executable step list (`{trigger, trigger_config, steps: [{id,
//    type, config, label}]}`, confirmed live via POST /v1/bot/flows/compile's
//    own journey_definition.steps) with no edges at all — there's no way to
//    know which steps belonged to which side of a branch once it's been
//    flattened like that, so this falls back to a best-effort LINEAR chain in
//    step order. A branch/condition step encountered this way is rendered as
//    a dead end (both arms marked "end") rather than guessed at, since
//    guessing wrong would silently misrepresent what the bot actually does.
export function chainFromSavedDefinition(definition: Record<string, any> | null | undefined): FlowNode | null {
  if (!definition) return null;

  if (Array.isArray(definition._canvas?.nodes)) {
    return chainFromJourneyFlow({ flow: definition._canvas } as JourneyFlowResult);
  }

  const steps: { id: string; type: string; config?: Record<string, any>; label?: string }[] = definition.steps;
  if (!Array.isArray(steps) || steps.length === 0) return null;

  let head: FlowNode | null = null;
  let tail: FlowNode | null = null;
  for (const step of steps) {
    if (TERMINAL_TYPES.has(step.type)) continue;
    const def = findActionDef(step.type);
    const node: FlowNode = {
      id: step.id,
      type: step.type,
      category: def?.category ?? "data",
      label: def?.nodeLabel || step.label || step.type,
      config: mapNodeConfig(step.type, step.config || {}),
      next: null,
    };
    if (BRANCHING_TYPES.has(step.type)) {
      node.branches = { true: { behavior: "end", next: null }, false: { behavior: "end", next: null } };
    }
    if (!head) head = node;
    else if (tail) tail.next = node;
    tail = node;
  }
  return head;
}
